import React, { useEffect, useRef } from 'react';
import LoadingSpinner from './LoadingSpinner';
import { ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/outline';

const EMPTY_SELECTION = new Set();

const Table = ({ 
  columns, 
  data, 
  loading, 
  emptyMessage = 'No data available',
  sortBy = null,
  sortOrder = 'asc',
  onSort = null,
  onRowClick = null,
  selectable = false,
  selectedIds = null,
  onToggleSelect = null,
  onToggleSelectAll = null,
  selectAllLabel = 'Select all rows',
  fixedLayout = false,
}) => {
  const selectAllRef = useRef(null);
  const rows = Array.isArray(data) ? data : [];
  const selected = selectedIds || EMPTY_SELECTION;
  const selectableRows = selectable ? rows.filter((row) => row && row.id) : [];
  const selectedOnPage = selectableRows.filter((row) => selected.has(row.id)).length;
  const allSelected = selectableRows.length > 0 && selectedOnPage === selectableRows.length;
  const someSelected = selectedOnPage > 0 && !allSelected;

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = someSelected;
    }
  }, [someSelected]);

  if (loading) {
    return (
      <div className="py-12">
        <LoadingSpinner />
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">{emptyMessage}</p>
      </div>
    );
  }

  const handleSort = (columnKey) => {
    if (!onSort) return;
    
    // If clicking the same column, toggle sort order
    if (sortBy === columnKey) {
      onSort(columnKey, sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // New column, default to ascending
      onSort(columnKey, 'asc');
    }
  };

  return (
    <div className="overflow-x-auto">
      <table className={`${fixedLayout ? 'w-full min-w-[1100px] table-fixed' : 'min-w-max w-full'} divide-y divide-gray-200`}>
        <thead className="bg-gray-50">
          <tr>
            {selectable && (
              <th className="px-3 py-3 w-10">
                <input
                  ref={selectAllRef}
                  type="checkbox"
                  checked={allSelected}
                  onChange={() => onToggleSelectAll && onToggleSelectAll(selectableRows)}
                  aria-label={selectAllLabel}
                  title={selectAllLabel}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 h-4 w-4"
                />
              </th>
            )}
            {columns.map((column) => (
              <th
                key={column.key}
                style={column.width ? { width: column.width, minWidth: column.minWidth || column.width } : undefined}
                className={`px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${
                  column.sortable !== false && onSort ? 'cursor-pointer hover:bg-gray-100 select-none' : ''
                } ${column.headerClassName || ''}`}
                onClick={() => column.sortable !== false && handleSort(column.key)}
              >
                <div className="flex items-center gap-1">
                  <span className="truncate">{column.label}</span>
                  {column.sortable !== false && onSort && (
                    <span className="flex-shrink-0">
                      {sortBy === column.key ? (
                        sortOrder === 'asc' ? (
                          <ChevronUpIcon className="w-3.5 h-3.5 text-primary-600" />
                        ) : (
                          <ChevronDownIcon className="w-3.5 h-3.5 text-primary-600" />
                        )
                      ) : (
                        <ChevronUpIcon className="w-3.5 h-3.5 text-gray-300" />
                      )}
                    </span>
                  )}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {data.map((row, rowIndex) => {
            const isSelected = selectable && row && selected.has(row.id);
            return (
            <tr 
              key={row.id || rowIndex} 
              className={`hover:bg-gray-50 ${isSelected ? 'bg-primary-50' : ''} ${onRowClick ? 'cursor-pointer' : ''}`}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
            >
              {selectable && (
                <td
                  className="px-3 py-3 w-10"
                  onClick={(e) => e.stopPropagation()}
                >
                  <input
                    type="checkbox"
                    checked={!!isSelected}
                    onChange={() => onToggleSelect && row.id && onToggleSelect(row.id)}
                    aria-label={`Select ${row.name || 'website'}`}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 h-4 w-4"
                  />
                </td>
              )}
              {columns.map((column) => (
                <td 
                  key={column.key} 
                  className={`px-3 py-3 text-sm text-gray-900 ${column.cellClassName || ''}`}
                  style={column.width ? { width: column.width, maxWidth: column.width } : undefined}
                >
                  {column.render ? column.render(row) : row[column.key]}
                </td>
              ))}
            </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default Table;

